import React, { useEffect, useState } from 'react';
import Timecode from 'smpte-timecode';
import styled from '@emotion/styled';
import { ClickAwayListener } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { findNextOverlayInLayer, getSMPTETimecode } from '@/components/Packaging/utils';
import DurationWrapper from '@/components/Packaging/LayeredTimeline/overlays/duration/DurationWrapper';
import { selectPackagingTimelineFrameRate } from '@/redux/selectors/packagingSelectors';
import { selectOverlayLayerByIndex, selectTimelineFramesCount } from '@/redux/selectors/packagingTimelineSelectors';
import { useToast } from '@/components/_common/Toast';
import { resizeOverlay } from '@/redux/slices/packagingTimelineSlice';
import { OverlayItem } from '@/redux/slices/packagingSlice.types';
import IconButtonStyled from '@/components/Packaging/common/IconButton';
import IconStyled from '@/components/Packaging/LayeredTimeline/overlays/duration/IconStyled';

interface Props {
  color: string;
  overlay: OverlayItem;
  layerIndex: number;
  hasAssignedInventory: boolean;
  className?: string;
  finishEdit(): void;
}

const DurationEdit: React.FC<Props> = ({ color, overlay, layerIndex, hasAssignedInventory, className, finishEdit }) => {
  const { draggableId: overlayId, start, end } = overlay;
  const duration = end - start;

  const dispatch = useAppDispatch();
  const frameRate = useAppSelector(selectPackagingTimelineFrameRate);
  const timelineFramesCount = useAppSelector(selectTimelineFramesCount);
  const currentOverlayLayer = useAppSelector((state) => selectOverlayLayerByIndex(state, layerIndex));

  const [isInvalid, setIsInvalid] = useState(false);
  const [inputValue, setInputValue] = useState(getSMPTETimecode(duration, frameRate));

  const { error } = useToast();

  const resetInput = () => {
    setInputValue(getSMPTETimecode(duration, frameRate));
    setIsInvalid(false);
  };

  useEffect(() => {
    resetInput();
  }, [duration]);

  const updateDuration = (timecodeStr: string) => {
    let timecode: Timecode.TimecodeInstance;

    try {
      timecode = new Timecode(timecodeStr, frameRate);
    } catch (e: any) {
      error(e.toString());
      setIsInvalid(true);
      return;
    }

    if (timecode.frameCount === 0) {
      error('Overlay should be at least one frame long');
      setIsInvalid(true);
      return;
    }

    const newEnd = start + timecode.frameCount;
    const nextOverlay = findNextOverlayInLayer(currentOverlayLayer, overlay);
    const maxEnd = nextOverlay ? nextOverlay.start : timelineFramesCount;
    const maxDurationFrames = maxEnd - start;

    if (newEnd > maxEnd) {
      error(
        <div>
          The maximum duration of the overlay in this position is{' '}
          <span style={{ whiteSpace: 'nowrap' }}>{getSMPTETimecode(maxDurationFrames, frameRate)}</span>
        </div>
      );
      setIsInvalid(true);
      return;
    }

    dispatch(
      resizeOverlay({
        overlayId,
        layerIndex,
        end: newEnd
      })
    );

    setIsInvalid(false);
    finishEdit();
  };

  const handleUpdateTimecodeButtonClick = () => {
    updateDuration(inputValue);
  };

  const handleClickAway = () => {
    finishEdit();
    resetInput();
  };

  const handleInputKeyDown: React.KeyboardEventHandler = (e) => {
    e.stopPropagation();

    switch (e.key) {
      case 'Escape':
        finishEdit();
        resetInput();
        break;
      case 'Enter':
        updateDuration(inputValue);
        break;
    }
  };

  const handleInputChange: React.FormEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.currentTarget.value);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <DurationWrapper className={className} color={color} hasAssignedInventory={hasAssignedInventory} isInvalid={isInvalid}>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: focuses timecode input when duration edit mode activates */}
        <EditField autoFocus value={inputValue} size={10} onInput={handleInputChange} onKeyDown={handleInputKeyDown} />
        <IconButtonStyled title="Save" onClick={handleUpdateTimecodeButtonClick}>
          <IconStyled icon="check" width={8} height={8} />
        </IconButtonStyled>
      </DurationWrapper>
    </ClickAwayListener>
  );
};

export default DurationEdit;

const EditField = styled.input`
  display: block;
  border: 0;
  font-size: inherit;
  line-height: inherit;
  margin-left: ${({ theme }) => theme.spacing(-0.5)};
  padding: ${({ theme }) => theme.spacing(0, 0.25, 0, 0.5)};
`;
